import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  PartyPopper,
  AlertCircle,
} from 'lucide-react';
import {
  BulkUploadJobView,
  BulkUploadJobStatus,
  BulkUploadDocumentStatus,
} from '../../../../api/General/bulkUpload';

interface ProcessingStepProps {
  job: BulkUploadJobView;
  onDone: () => void;
}

export default function ProcessingStep({ job, onDone }: ProcessingStepProps) {
  const { t } = useTranslation('common');

  const isProcessing = job.status === BulkUploadJobStatus.Processing;
  const isCompleted = job.status === BulkUploadJobStatus.Completed;
  const isFailed = job.status === BulkUploadJobStatus.Failed;
  const isDone = isCompleted || isFailed;

  const totalToProcess = job.documents?.filter(
    (d) =>
      d.status === BulkUploadDocumentStatus.Validated ||
      d.status === BulkUploadDocumentStatus.Processing ||
      d.status === BulkUploadDocumentStatus.Completed ||
      d.status === BulkUploadDocumentStatus.Failed
  ).length ?? 0;

  const progress = totalToProcess > 0 ? (job.processedFiles / totalToProcess) * 100 : 0;

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="mb-6 text-center">
        {isProcessing && (
          <>
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground">{t('Processing Documents')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('Linking documents to employee profiles. Please wait...')}
            </p>
          </>
        )}
        {isCompleted && (
          <>
            <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-full bg-green-100 dark:bg-green-900/30">
              <PartyPopper className="w-7 h-7 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">{t('Processing Complete')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('All documents have been successfully linked to employee profiles.')}
            </p>
          </>
        )}
        {isFailed && (
          <>
            <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              {t('Processing Completed with Errors')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('Some documents could not be processed. See details below.')}
            </p>
          </>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">
            {t('Progress')}
          </span>
          <span className="text-sm text-muted-foreground">
            {job.processedFiles}/{totalToProcess} {t('files')}
          </span>
        </div>
        <div className="w-full h-3 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ease-out ${
              isFailed
                ? 'bg-red-500'
                : isCompleted
                ? 'bg-green-500'
                : 'bg-primary'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="text-center p-3 rounded-lg bg-muted/50">
          <p className="text-2xl font-bold text-foreground">{job.processedFiles}</p>
          <p className="text-xs text-muted-foreground">{t('Processed')}</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-green-50 dark:bg-green-900/10">
          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
            {job.successfulFiles}
          </p>
          <p className="text-xs text-green-600 dark:text-green-500">{t('Successful')}</p>
        </div>
        <div className="text-center p-3 rounded-lg bg-red-50 dark:bg-red-900/10">
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">{job.failedFiles}</p>
          <p className="text-xs text-red-600 dark:text-red-500">{t('Failed')}</p>
        </div>
      </div>

      {/* Per-file status list */}
      {job.documents && job.documents.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
          <div className="divide-y divide-border">
            {job.documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-3 px-4 py-2.5 text-sm"
              >
                <div className="shrink-0">
                  {doc.status === BulkUploadDocumentStatus.Completed && (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  )}
                  {doc.status === BulkUploadDocumentStatus.Failed && (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  {doc.status === BulkUploadDocumentStatus.Processing && (
                    <Loader2 className="w-4 h-4 text-primary animate-spin" />
                  )}
                  {(doc.status === BulkUploadDocumentStatus.Validated ||
                    doc.status === BulkUploadDocumentStatus.Mapped ||
                    doc.status === BulkUploadDocumentStatus.Uploaded) && (
                    <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-foreground">{doc.originalFileName}</p>
                  {doc.userFullName && (
                    <p className="text-xs text-muted-foreground truncate">
                      → {doc.userFullName}
                      {doc.companyAttachmentTypeName ? ` · ${doc.companyAttachmentTypeName}` : ''}
                    </p>
                  )}
                </div>
                {doc.status === BulkUploadDocumentStatus.Failed && doc.errorMessage && (
                  <span className="text-xs text-red-500 truncate max-w-[200px]">
                    {doc.errorMessage}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Done button */}
      {isDone && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={onDone}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {t('Done')}
          </button>
        </div>
      )}
    </div>
  );
}
